import _ from 'underscore';

import {NucleotideType, NUCLEOTIDE_TYPE_TO_COMPLEMENT_TABLE} from 'biojs/BioConstants';
import * as util from 'biojs/Util';
import {Strand} from 'constants/MolbioConstants';
import {SequenceType} from 'constants/SequenceConstants';
import * as sequtil from 'molbio/SeqUtil';

export interface SequenceMatch {
  index: number;
  strand: Strand;
  sequence?: string;
}

export interface SearchOptions {
  allowedChars: Array<string>;
  range: {start: number; end: number} | null;
  limit: number;
  dedup: boolean;
  aminoAcids: boolean;
  maxMismatches: number;
  includeMatch: boolean;
  filter: (match: SequenceMatch) => boolean;
}

/**
 *
 * @param seq the residue pattern to search for, which may be bases or amino acids
 * @param sequenceBases the nucleotide bases to search over
 * @param nucleotideType the nucleotide type of the bases being searched over
 * @param sequenceType the sequence type of the bases being searched over
 * @param circular the circularity of the bases being searched over
 * @param options search options
 */
export function search(
  seq: string,
  sequenceBases: string,
  nucleotideType: NucleotideType,
  sequenceType: SequenceType,
  circular: boolean,
  options?: Partial<SearchOptions>
): Array<SequenceMatch> {
  let pattern;
  let revPattern;
  if (options == null) {
    options = {};
  }
  _.defaults(options, {
    allowedChars: [],
    range: null,
    limit: Infinity,
    dedup: false,
    aminoAcids: false,
    maxMismatches: 0,
    includeMatch: true,
    filter(match: SequenceMatch): boolean {
      return true;
    },
  });

  const {allowedChars, range, limit, dedup, aminoAcids, maxMismatches, filter, includeMatch} = options;
  // only respect mismatches if not AA
  const isWithMismatches = !aminoAcids && maxMismatches !== 0;
  const baseTable = NUCLEOTIDE_TYPE_TO_COMPLEMENT_TABLE[nucleotideType];
  if (aminoAcids) {
    if (nucleotideType === NucleotideType.RNA) {
      pattern = util.generateAARegexForRNA(seq);
    } else {
      pattern = util.generateAARegexForDNA(seq);
    }
    // Reverse patterns for RNA Sequences are filtered out below.
    revPattern = util.generateAARegexForDNA(seq, {revcomp: true});
  } else {
    const dnaSeq = util.reverseTranscribeRNA(seq);
    pattern = util.generateRegex(dnaSeq, {
      allowedChars,
      maxMismatches,
      rnaAgnostic: true,
    });
    revPattern = util.generateRegex(util.revcomp(dnaSeq, baseTable), {
      allowedChars,
      maxMismatches,
    });
  }

  let searchString = '';
  let offset = 0;

  const fragments = [];
  if (range != null) {
    if (!circular && sequtil.isWrap(range.start, range.end)) {
      fragments.push({
        searchString: util.slice(sequenceBases, range.start, 0, circular),
        offset: range.start,
      });
      fragments.push({
        searchString: util.slice(sequenceBases, 0, range.end, circular),
        offset: 0,
      });
    } else {
      fragments.push({
        searchString: util.slice(sequenceBases, range.start, range.end, circular),
        offset: range.start,
      });
    }
  } else if (circular) {
    const overlapLength = aminoAcids ? seq.length * 3 : seq.length;
    const basesOverOrigin = Math.min(overlapLength, sequenceBases.length) - 1;
    fragments.push({
      searchString: sequenceBases + sequenceBases.slice(0, basesOverOrigin),
      offset: 0,
    });
  } else {
    fragments.push({
      searchString: sequenceBases,
      offset: 0,
    });
  }

  const length = sequenceBases.length;

  interface MatchGen {
    match: SequenceMatch | null;
    index: number | null;
    madeMatch: boolean;
    done: boolean;
    popMatch: () => SequenceMatch;
    makeMatch: () => boolean;
    hasMatch: () => boolean;
  }

  // Fwd and rev match are done "in parallel" so that results are already sorted,
  // and limited results are denser (closer together)
  function matchGen(
    _pattern: RegExp | util.ImitationRegExp,
    strand: Strand,
    seqFn: (seq: string) => string
  ): MatchGen {
    return {
      match: null,
      index: null,
      madeMatch: false,
      done: false,
      popMatch(): SequenceMatch {
        const {match} = this;
        this.match = null;
        this.index = null;
        this.madeMatch = false;
        if (match == null) {
          throw new Error('No matches left to pop');
        }
        return match;
      },
      makeMatch(): boolean {
        let match;
        while (true) {
          match = _pattern.exec(searchString);
          if (match == null) {
            break;
          }
          _pattern.lastIndex = match.index + 1;
          const newMatch: SequenceMatch = {
            index: sequtil.mod(match.index + offset, length),
            strand,
          };
          if (includeMatch) {
            newMatch.sequence = seqFn(match[0]);
          }
          // @ts-expect-error _.defaults populates filter
          if (filter(newMatch)) {
            this.match = newMatch;
            this.index = match.index;
            this.madeMatch = true;
            return this.madeMatch;
          }
        }
        this.match = null;
        this.index = null;
        this.done = true;
        this.madeMatch = false;
        return this.madeMatch;
      },
      hasMatch(): boolean {
        return this.madeMatch || (!this.done && this.makeMatch());
      },
    };
  }

  const results = [];
  for ({searchString, offset} of Array.from(fragments)) {
    if (isWithMismatches) {
      searchString = searchString.toUpperCase(); // generated pattern is always uppercase
    }
    const fwdMatcher = matchGen(pattern, 1, (sequence) => sequence);
    const revMatcher =
      sequenceType === 'ds'
        ? matchGen(revPattern, -1, (sequence) => util.revcomp(sequence, baseTable))
        : {
            hasMatch(): boolean {
              return false;
            },
          };

    // @ts-expect-error _.defaults populates limit
    while (results.length < limit) {
      const fwdHasMatch = fwdMatcher.hasMatch();
      const revHasMatch = revMatcher.hasMatch();
      if (!fwdHasMatch && !revHasMatch) {
        break;
      }

      // Find which has a lower index / is not non-existent
      const nextMatch = (() => {
        if (!revHasMatch || (fwdHasMatch && fwdMatcher.index! < (revMatcher as MatchGen).index!)) {
          return fwdMatcher.popMatch();
        } else if (!fwdHasMatch || (revHasMatch && fwdMatcher.index! > (revMatcher as MatchGen).index!)) {
          return (revMatcher as MatchGen).popMatch();
        } else {
          // same index
          const match = fwdMatcher.popMatch();
          if (!dedup) {
            (revMatcher as MatchGen).popMatch(); // ignore the matching match.
            match.strand = 0;
          }
          return match;
        }
      })();
      results.push(nextMatch);
    }
  }
  return results;
}
